import { useEffect, useState, type ReactNode } from 'react';
import { useSupportConfig } from '../SupportErrorProvider';

/**
 * Wizard de bienvenida que presenta el nuevo asistente de soporte con IA.
 * Aparece una sola vez por usuario (se recuerda en localStorage) y solo cuando
 * el asistente está habilitado para el proyecto. Subí SEEN_KEY de versión si
 * querés volver a mostrarlo tras un cambio grande de la funcionalidad.
 */
const SEEN_KEY = 'fl-support-welcome-seen-v1';

interface WizardStep {
  icon: ReactNode;
  title: string;
  body: string;
}

const STEPS: WizardStep[] = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-8 w-8">
        <path d="M12 2a7 7 0 0 0-4 12.75V17a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2.25A7 7 0 0 0 12 2Z" />
        <path d="M9 21h6M10 18v3M14 18v3" />
      </svg>
    ),
    title: '¡Conoce tu nuevo asistente de soporte!',
    body: 'Sumamos a FacturaLibre un asistente con inteligencia artificial que te ayuda al instante, sin salir del portal. Nada de esperar: pregúntale y te responde.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-8 w-8">
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01" />
      </svg>
    ),
    title: 'Pregunta lo que necesites',
    body: 'Toca el botón flotante de ayuda (abajo a la izquierda), escribe tu duda o describe el problema, y el asistente te responde al instante.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-8 w-8">
        <polygon points="3 11 22 2 13 21 11 13 3 11" />
      </svg>
    ),
    title: 'Te guío en pantalla',
    body: 'Si hace falta, el asistente te muestra el camino paso a paso: te va señalando dónde tocar dentro del portal hasta resolver lo que buscas.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-8 w-8">
        <path d="M20 6 9 17l-5-5" />
      </svg>
    ),
    title: 'Siempre a mano',
    body: 'El botón de ayuda está disponible en todo momento y puedes arrastrarlo a donde te quede cómodo. ¿Listo para probarlo?',
  },
];

export function SupportWelcomeWizard() {
  const { assistantEnabled } = useSupportConfig();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  // Mostrar una sola vez, y solo si el asistente está encendido.
  useEffect(() => {
    if (!assistantEnabled) return;
    try {
      if (!localStorage.getItem(SEEN_KEY)) setOpen(true);
    } catch {
      /* localStorage no disponible: no forzamos el modal */
    }
  }, [assistantEnabled]);

  function dismiss() {
    setOpen(false);
    try { localStorage.setItem(SEEN_KEY, '1'); } catch { /* ignore */ }
  }

  // Cerrar con Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') dismiss(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  const isLast = step === STEPS.length - 1;
  const current = STEPS[step];

  return (
    <div
      data-support-ui
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
      onClick={dismiss}
      role="dialog"
      aria-modal="true"
      aria-labelledby="support-welcome-title"
    >
      <div
        className="flex max-h-[92vh] w-full max-w-md flex-col overflow-y-auto rounded-2xl bg-white shadow-hard"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Hero de marca */}
        <div className="relative flex flex-col items-center gap-3 bg-gradient-to-br from-primary-700 to-primary-900 px-6 pb-8 pt-9 text-center text-white">
          <button
            onClick={dismiss}
            aria-label="Cerrar"
            className="absolute right-3 top-3 rounded-lg p-1 text-white/70 transition hover:bg-white/10 hover:text-white"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="h-5 w-5">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
          <span className="mb-1 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide">Novedad</span>
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/15 text-white">
            {current.icon}
          </div>
        </div>

        {/* Contenido del paso */}
        <div className="flex flex-col gap-3 px-6 pb-2 pt-6 text-center">
          <h2 id="support-welcome-title" className="text-lg font-bold text-secondary-900">
            {current.title}
          </h2>
          <p className="text-sm leading-relaxed text-secondary-600">{current.body}</p>
        </div>

        {/* Indicador de pasos (puntos) */}
        <div className="flex justify-center gap-2 py-4">
          {STEPS.map((_, i) => (
            <button
              key={i}
              onClick={() => setStep(i)}
              aria-label={`Ir al paso ${i + 1}`}
              className={
                'h-2 rounded-full transition-all ' +
                (i === step ? 'w-6 bg-primary-400' : 'w-2 bg-secondary-200 hover:bg-secondary-300')
              }
            />
          ))}
        </div>

        {/* Navegación */}
        <div className="flex items-center justify-between gap-3 border-t border-secondary-100 px-6 py-4">
          {step > 0 ? (
            <button
              onClick={() => setStep((s) => s - 1)}
              className="rounded-lg px-3 py-2 text-sm font-medium text-secondary-600 transition hover:bg-secondary-50"
            >
              Atrás
            </button>
          ) : (
            <button
              onClick={dismiss}
              className="rounded-lg px-3 py-2 text-sm font-medium text-secondary-500 transition hover:bg-secondary-50"
            >
              Omitir
            </button>
          )}

          <button
            onClick={() => (isLast ? dismiss() : setStep((s) => s + 1))}
            className="rounded-lg bg-primary-700 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-800"
          >
            {isLast ? '¡Empezar!' : 'Siguiente'}
          </button>
        </div>
      </div>
    </div>
  );
}
